/**
 * How a fact reads on the facts screens (S12 onboarding, account Business details): plain, labelled text a person
 * can read, never JSON and never a raw key. Pure: the path and value in, a display shape out, so every pilot fact
 * shape is unit tested (facts.test.ts) and the FactValue component only lays it out.
 */
import { S, list } from '@ff/app-ui/copy';
import { phoneLabel } from './format.ts';

export type FactDisplay =
  | { kind: 'text'; text: string }
  | { kind: 'lines'; lines: string[] }
  | { kind: 'list'; items: string[] }
  /** Opening hours: days and times, one row each. */
  | { kind: 'table'; rows: Array<[string, string]> }
  /** A definition list: humanised labels and readable values. */
  | { kind: 'pairs'; rows: Array<[string, string]> };

const C = S.facts;
const E164 = /^\+1\d{10}$/;
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})(?:T[\d:.]+Z?)?$/;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v);
const str = (v: unknown): v is string => typeof v === 'string' && v.length > 0;

/** "2026-09-24" or an ISO timestamp → "Sep 24, 2026" (read as a calendar day, so it never slips across zones). */
export function isoDay(s: string): string | null {
  const m = ISO_DAY.exec(s);
  if (!m) return null;
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return `${MONTHS[month - 1]} ${day}, ${m[1]}`;
}

/** "time-on-site" → "Time on site", "zipCode" → "Zip code", "general_liability" → "General liability". */
export function humanKey(k: string): string {
  const words = k
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_\-.]+/g, ' ')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Any scalar or short array as one readable line. */
export function plain(v: unknown): string {
  if (v === null || v === undefined || v === '') return C.notSet;
  if (typeof v === 'boolean') return v ? C.yes : C.no;
  if (typeof v === 'number') return Number.isInteger(v) && Math.abs(v) < 10_000 ? String(v) : v.toLocaleString('en-US');
  if (typeof v === 'string') return E164.test(v) ? phoneLabel(v) : (isoDay(v) ?? v);
  if (Array.isArray(v)) return list(v.map(plain));
  if (isObj(v)) return Object.entries(v).map(([k, x]) => `${humanKey(k)}: ${plain(x)}`).join(', ');
  return String(v);
}

const pairs = (rows: Array<[string, unknown]>): FactDisplay => ({ kind: 'pairs', rows: rows.filter(([, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => [k, plain(v)]) });

/** An array reads as a comma list when it's short (brands, a few towns), else as a list. */
function arrayDisplay(path: string, v: unknown[]): FactDisplay {
  const items = v.map(plain);
  const short = items.length <= 6 && items.every((x) => x.length <= 32);
  return path.endsWith('.brands') || short ? { kind: 'text', text: list(items) } : { kind: 'list', items };
}

function objectDisplay(path: string, v: Obj): FactDisplay {
  const head = path.split('.')[0]!;
  const keys = Object.keys(v);
  if (path === MONEY_SERVICES_PATH) return moneyServices(v);
  // Address: two lines
  if (str(v.street) && str(v.city) && str(v.state)) return { kind: 'lines', lines: [v.street, `${v.city}, ${v.state}${v.zip ? ` ${String(v.zip)}` : ''}`] };
  // Opening hours: a small table of days and times
  if (head === 'hours' && keys.length && keys.every((k) => typeof v[k] === 'string')) return { kind: 'table', rows: keys.map((k) => [k, /^closed$/i.test(String(v[k])) ? C.closed : String(v[k])]) };
  // A rating from a review platform
  if (typeof v.rating === 'number' && typeof v.count === 'number') return { kind: 'text', text: C.stars(v.rating.toFixed(1), v.count.toLocaleString('en-US'), str(v.platform) ? v.platform : null) };
  if (head === 'licences' || head === 'licenses' || head === 'licence' || head === 'license') return pairs([[C.licenseType, v.class], [C.number, v.number], [C.issuedBy, v.board], ...rest(v, ['class', 'number', 'board', 'state'])]);
  if (head === 'offers' && str(v.title)) return pairs([[C.deal, str(v.figure) ? `${v.figure} ${v.title}` : v.title], [C.terms, v.terms], [C.ends, v.expires], ...rest(v, ['figure', 'title', 'terms', 'expires'])]);
  if (head === 'financing' && str(v.lender)) return pairs([[C.lender, v.lender], [C.inShort, v.line], [C.finePrint, v.disclosure], ...rest(v, ['lender', 'line', 'disclosure'])]);
  if (head === 'certs' && str(v.name)) return { kind: 'lines', lines: [v.name, ...(str(v.issuer) && v.issuer !== v.name ? [v.issuer] : [])] };
  if (head === 'promise' && str(v.line)) return { kind: 'lines', lines: [v.line, ...(str(v.detail) ? [v.detail] : [])] };
  if (head === 'team' && str(v.name)) return pairs([[C.name, v.name], [C.role, v.role], [C.since, v.since], [C.about, v.bio], ...rest(v, ['name', 'role', 'since', 'bio'])]);
  return pairs(keys.map((k) => [humanKey(k), v[k]]));
}

/** The three money services (c010): a record of service slug to the search we measure for it. */
export const MONEY_SERVICES_PATH = 'measure.money-services';

/** The searches, one a line, in the agreed order. The slugs are ours and never shown. */
function moneyServices(v: Obj): FactDisplay {
  const lines = Object.values(v).filter(str).map((x) => x.trim()).filter((x) => x.length > 0);
  return lines.length ? { kind: 'lines', lines } : { kind: 'text', text: C.notSet };
}

/**
 * A fact's label where the app knows a better one than the path gives: "Your three money services". Anything else
 * keeps the label the server sent.
 */
export function factLabel(path: string, fallback: string): string {
  return path === MONEY_SERVICES_PATH ? S.reports.moneyServices : fallback;
}

const rest = (v: Obj, known: string[]): Array<[string, unknown]> => Object.keys(v).filter((k) => !known.includes(k)).map((k) => [humanKey(k), v[k]]);

/** The display shape for one fact. */
export function displayFact(path: string, value: unknown): FactDisplay {
  if (value === null || value === undefined || value === '') return { kind: 'text', text: C.notSet };
  if (typeof value === 'boolean') {
    if (/^services?\..+\.offered$/.test(path)) return { kind: 'text', text: value ? C.offered : C.notOffered };
    return { kind: 'text', text: value ? C.yes : C.no };
  }
  if (typeof value === 'number') {
    if (/^coverage\.radius/.test(path)) return { kind: 'text', text: C.miles(plain(value)) };
    return { kind: 'text', text: plain(value) };
  }
  if (typeof value === 'string') return { kind: 'text', text: plain(value) };
  if (Array.isArray(value)) return arrayDisplay(path, value);
  if (isObj(value)) return objectDisplay(path, value);
  return { kind: 'text', text: String(value) };
}

/** The display as one line of text (for a screen reader summary or a single field). */
export function displayText(d: FactDisplay): string {
  switch (d.kind) {
    case 'text':
      return d.text;
    case 'lines':
      return d.lines.join(', ');
    case 'list':
      return list(d.items);
    default:
      return d.rows.map(([k, v]) => `${k}: ${v}`).join(', ');
  }
}

/**
 * What the edit field starts with: the readable value for a single line fact (a phone reads "(303) 555-0142"), or
 * null for a structured one (an address, hours, a license), where the screen asks for the change in words instead.
 */
export function editDefault(_path: string, value: unknown): string | null {
  // Only text facts are edited in place. A number, list, yes or no, or a structured value keeps its shape: the site
  // reads `business.founded` as a number and brands as a list, so the words go to our team as a note instead.
  return typeof value === 'string' ? plain(value) : null;
}

/**
 * The value an in-place edit sends. A phone shown as "(303) 555-0142" goes back as E.164 when the original was, so
 * the site's phone check still passes; anything else is the text as typed.
 */
export function editValue(original: unknown, input: string): string {
  const text = input.trim();
  if (typeof original === 'string' && E164.test(original)) {
    const digits = text.replace(/\D/g, '');
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  }
  return text;
}

/** "Checked Sep 20 from your Google profile": the source label and the day, small under the value. */
export function provenance(source: { label: string }, checkedAt: string): string {
  const day = isoDay(checkedAt);
  const short = day ? day.replace(/, \d{4}$/, '') : checkedAt;
  const from = source.label ? source.label.charAt(0).toLowerCase() + source.label.slice(1) : '';
  return C.checked(short, from).trim();
}
